import { useLocation, useNavigate } from "react-router-dom";
import { moduleKeyForWebPath } from "@campushubai/shared";
import { NavLayers as Layers } from "@/components/icons/solid-icons";
import { ArrowLeftFill as ArrowLeft, LoaderFill as Loader2 } from "@/components/icons/solid-extra-icons";
import { useAuthStore } from "@/store/auth-store";
import { useModuleAccess } from "@/hooks/use-module-access";
import { ModuleTeaserPage } from "@/components/modules/module-teaser";
import "@/components/gates-native.css";
import "@/components/modules/module-lock.css";

interface RequireEntitlementProps {
  children: React.ReactNode;
}

// Roles this gate never applies to: platform staff spans every tenant by
// design. (An overseer with no school picked is handled by the API: it
// answers with the UNION over their schools, so a module that is on in any
// of them is "visible" here and the data layer narrows per school.)
const EXEMPT_ROLES = new Set(["CONTROL_CENTER"]);

/**
 * Route-level companion to the nav filters. The menus hide (or lock) a module
 * the school has switched off; this catches the same module reached by a
 * direct URL, a bookmark, a stale link or a notification tap.
 *
 *   on      -> the page.
 *   locked  -> ADMIN / PRINCIPAL / CHAIRMAN / DIRECTOR: an "Upgrade" teaser.
 *              No module code is rendered, so no data is fetched.
 *   hidden  -> everyone else: a plain "not available" page, as if the
 *              module did not exist for their school.
 *
 * The API's own gate (`plugins/module-entitlements.ts`) is the enforcement
 * boundary; this is the friendly landing page in front of the 403 it would
 * give anyway.
 */
export function RequireEntitlement({ children }: RequireEntitlementProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const role = useAuthStore((s) => s.user?.role ?? "");
  const access = useModuleAccess();

  const moduleKey = moduleKeyForWebPath(location.pathname);
  if (!moduleKey) return <>{children}</>; // core / uncatalogued path — not gated
  if (EXEMPT_ROLES.has(role)) return <>{children}</>;

  // Avoid a flash of the gate before the first fetch resolves.
  if (access.isLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" role="status" aria-label="Loading">
        <Loader2 size={22} className="animate-spin text-[var(--color-text-4)]" />
      </div>
    );
  }

  const visibility = access.visibility(moduleKey);
  if (visibility === "visible") return <>{children}</>;

  if (visibility === "locked") {
    return <ModuleTeaserPage moduleKey={moduleKey} planName={access.data?.planName} onBack={() => navigate("/dashboard")} />;
  }

  return (
    <div className="ch-teaser" data-module-gate="hidden">
      <section className="ch-teaser__card" aria-labelledby="ent-gate-title">
        <header className="ch-teaser__head">
          <span className="ch-teaser__icon" aria-hidden>
            <Layers size={20} />
          </span>
          <div className="min-w-0">
            <p className="ch-teaser__eyebrow">Not available</p>
            <h1 id="ent-gate-title" className="ch-teaser__title">This page isn't available</h1>
          </div>
        </header>
        <div className="ch-teaser__body">
          <p>This feature isn't enabled for your school. If you followed an old link or notification, head back to your dashboard.</p>
        </div>
        <footer className="ch-teaser__foot">
          <button type="button" className="sd-btn sd-btn--primary" onClick={() => navigate("/dashboard")}>
            <ArrowLeft size={15} />
            Back to dashboard
          </button>
        </footer>
      </section>
    </div>
  );
}
